import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Pencil } from "lucide-react";

export default function Boards() {
  const [boards, setBoards] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/boards").then((r) => setBoards(r.data));
  }, []);

  return (
    <div className="space-y-8 fade-in" data-testid="boards-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Boards</div>
          <h1 className="font-display text-4xl tracking-tight mt-2">Ranglisten · Kanban</h1>
          <p className="text-white/60 text-sm mt-2">Frei konfigurierbare Vereins-Boards · nur Admins bearbeiten.</p>
        </div>
        {user?.role === "admin" && (
          <Link to="/admin/boards" className="btn-primary" data-testid="manage-boards-btn">
            <Pencil className="w-4 h-4" /> Boards verwalten
          </Link>
        )}
      </div>

      {boards.length === 0 ? (
        <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          Noch keine Boards.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.map((b) => (
            <Link
              key={b.id}
              to={`/boards/${b.id}`}
              data-testid={`board-link-${b.id}`}
              className="card-solid overflow-hidden hover:border-primary/50 transition-colors group relative"
            >
              {b.wallpaper_url && (
                <div className="relative h-32 overflow-hidden">
                  <img src={b.wallpaper_url} alt="" className="w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                </div>
              )}
              <div className="p-5">
                <div className="font-display text-xl tracking-tight group-hover:text-gradient">{b.title}</div>
                <div className="text-xs text-white/50 mt-1">{b.description}</div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {b.columns?.slice(0, 4).map((c) => (
                    <span key={c.id} className="chip text-white/60">{c.title}</span>
                  ))}
                  {b.columns?.length > 4 && <span className="chip text-white/40">+{b.columns.length - 4}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BoardDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const isAdmin = user?.role === "admin";
  const [draggedCard, setDraggedCard] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/boards/${id}`);
      setBoard(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const dropOnColumn = async (colId) => {
    if (!isAdmin || !draggedCard) return;
    const target = (board.cards || []).filter((c) => c.column_id === colId);
    const order = target.length;
    try {
      await api.put(`/boards/${id}/cards/${draggedCard.id}/move`, { column_id: colId, order });
      setDraggedCard(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (error) return <div className="font-mono text-xs text-red-400">{error}</div>;
  if (!board) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="board-detail-page">
      <Link to="/boards" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-primary link-underline">← Boards</Link>
      <div className="relative card-solid overflow-hidden">
        {board.wallpaper_url && (
          <div className="absolute inset-0 -z-0">
            <img src={board.wallpaper_url} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-bg/70" />
          </div>
        )}
        <div className="relative p-6">
          <div className="font-display text-3xl tracking-tight">{board.title}</div>
          <div className="text-white/60 text-sm mt-1">{board.description}</div>
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 pb-4">
        <div className="flex gap-4 min-w-max">
          {(board.columns || []).map((col) => {
            const cards = (board.cards || []).filter((c) => c.column_id === col.id).sort((a, b) => a.order - b.order);
            return (
              <div
                key={col.id}
                onDragOver={(e) => isAdmin && e.preventDefault()}
                onDrop={() => dropOnColumn(col.id)}
                className="w-80 shrink-0 card-solid p-4 min-h-[24rem]"
                data-testid={`column-${col.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Column</div>
                    <div className="font-display text-lg tracking-tight">{col.title}</div>
                  </div>
                  <span className="chip text-white/40">{cards.length}</span>
                </div>
                <div className="space-y-3">
                  {cards.map((c) => (
                    <div
                      key={c.id}
                      draggable={isAdmin}
                      onDragStart={() => setDraggedCard(c)}
                      data-testid={`card-${c.id}`}
                      className="card p-3 cursor-grab active:cursor-grabbing border-l-4 hover:bg-white/5 transition-colors"
                      style={{ borderColor: c.color || "#29E9FF" }}
                    >
                      <div className="flex items-start gap-3">
                        {c.image_url && (
                          <img src={c.image_url} alt="" className="w-10 h-10 object-cover rounded-sm border border-white/10" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-sm tracking-tight truncate">{c.title}</div>
                          {c.content && <div className="text-xs text-white/60 mt-1 line-clamp-2">{c.content}</div>}
                          {c.stats && (
                            <div className="grid grid-cols-3 gap-1 mt-2">
                              {Object.entries(c.stats).slice(0, 3).map(([k, v]) => (
                                <div key={k} className="text-center bg-white/5 py-1">
                                  <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/40">{k}</div>
                                  <div className="font-mono tabular-nums text-xs text-primary">{v}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && (
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 text-center py-6 border border-dashed border-white/10">
                      Leer
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {isAdmin && (
        <div className="text-xs font-mono text-white/40">
          Tipp: Karten per Drag & Drop zwischen Spalten verschieben. Boards verwalten →{" "}
          <button onClick={() => navigate("/admin/boards")} className="text-primary link-underline" data-testid="open-board-admin">
            Admin Dashboard
          </button>
        </div>
      )}
    </div>
  );
}
